import React from 'react';
import { MailOutlined } from '@ant-design/icons';
import './PrivacyPolicyPage.css';

/*
 * Public page (linked from the app stores and the mobile app). The policy text itself is English only
 * and is kept word for word — only the presentation is ours.
 */

type Definition = { term: string; definition: string };

const DEFINITIONS: Definition[] = [
  { term: 'Account', definition: 'means a unique account created for You to access our Service or parts of our Service.' },
  { term: 'Affiliate', definition: 'means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.' },
  { term: 'Application', definition: 'refers to Estethica22, the software program provided by the Company.' },
  { term: 'Company', definition: '(referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Estethica22.' },
  { term: 'Cookies', definition: 'are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.' },
  { term: 'Country', definition: 'refers to: Bayern, Germany' },
  { term: 'Device', definition: 'means any device that can access the Service such as a computer, a cellphone or a digital tablet.' },
  { term: 'Personal Data', definition: 'is any information that relates to an identified or identifiable individual.' },
  { term: 'Service', definition: 'refers to the Application or the Website or both.' },
  { term: 'Service Provider', definition: 'means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.' },
  { term: 'Usage Data', definition: 'refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).' },
  { term: 'Website', definition: 'refers to Estethica22, accessible from https://estethica22.com/login' },
  { term: 'You', definition: 'means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.' },
];

const TRACKING: Definition[] = [
  {
    term: 'Cookies or Browser Cookies',
    definition: 'A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies.',
  },
  {
    term: 'Web Beacons',
    definition: 'Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity).',
  },
];

const SECTIONS = [
  { id: 'definitions', title: 'Interpretation and Definitions' },
  { id: 'personal-data', title: 'Collecting and Using Your Personal Data' },
  { id: 'application', title: 'Information Collected while Using the Application' },
  { id: 'tracking', title: 'Tracking Technologies and Cookies' },
  { id: 'contact', title: 'Contact Us' },
];

const CONTACT_EMAIL = 'estethica.22@gmail.com';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="pp">
      <header className="pp-topbar">
        <div className="pp-topbar-inner">
          <img src="/estethica-logo.png" alt="Estethica22" className="pp-topbar-logo" />
        </div>
      </header>

      <div className="pp-hero">
        <div className="pp-hero-inner">
          <h1 className="pp-title">Privacy Policy</h1>
          <div className="pp-updated">Last updated: May 25, 2025</div>
        </div>
      </div>

      <div className="pp-layout">
        <nav className="pp-toc" aria-label="Contents">
          <div className="pp-toc-label">Contents</div>
          <ol>
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="pp-article e22-card">
          <p className="pp-lead">
            This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
          </p>
          <p>
            We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. This Privacy Policy has been created with the help of the Privacy Policy Generator.
          </p>

          <section id="definitions" className="pp-section">
            <h2>Interpretation and Definitions</h2>
            <h3>Interpretation</h3>
            <p>
              The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
            </p>

            <h3>Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            <dl className="pp-defs">
              {DEFINITIONS.map((item) => (
                <div key={item.term} className="pp-def">
                  <dt>{item.term}</dt>
                  <dd>{item.definition}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section id="personal-data" className="pp-section">
            <h2>Collecting and Using Your Personal Data</h2>
            <h3>Types of Data Collected</h3>
            <h4>Personal Data</h4>
            <p>
              While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
            </p>
            <ul className="pp-list">
              <li>Email address</li>
              <li>Usage Data</li>
            </ul>

            <h4>Usage Data</h4>
            <p>Usage Data is collected automatically when using the Service.</p>
            <p>
              Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
            </p>
            <p>
              When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.
            </p>
            <p>
              We may also collect information that Your browser sends whenever You visit our Service or when You access the Service by or through a mobile device.
            </p>
          </section>

          <section id="application" className="pp-section">
            <h2>Information Collected while Using the Application</h2>
            <p>
              While using Our Application, in order to provide features of Our Application, We may collect, with Your prior permission:
            </p>
            <ul className="pp-list">
              <li>Information regarding your location</li>
              <li>Pictures and other information from your Device's camera and photo library</li>
            </ul>
            <p>
              We use this information to provide features of Our Service, to improve and customize Our Service. The information may be uploaded to the Company's servers and/or a Service Provider's server or it may be simply stored on Your device.
            </p>
            <p>You can enable or disable access to this information at any time, through Your Device settings.</p>
          </section>

          <section id="tracking" className="pp-section">
            <h2>Tracking Technologies and Cookies</h2>
            <p>
              We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service. The technologies We use may include:
            </p>
            <dl className="pp-defs pp-defs--stacked">
              {TRACKING.map((item) => (
                <div key={item.term} className="pp-def">
                  <dt>{item.term}.</dt>
                  <dd>{item.definition}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section id="contact" className="pp-section">
            <h2>Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, You can contact us:</p>
            <a className="pp-contact" href={`mailto:${CONTACT_EMAIL}`}>
              <span className="pp-contact-icon" aria-hidden>
                <MailOutlined />
              </span>
              <span>
                <span className="pp-contact-label">By email</span>
                <span className="pp-contact-value">{CONTACT_EMAIL}</span>
              </span>
            </a>
          </section>
        </article>
      </div>

      <footer className="pp-footer">© {new Date().getFullYear()} Estethica22</footer>
    </div>
  );
};

export default PrivacyPolicyPage;
